import React, { useEffect } from 'react';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import Button from '@atlaskit/button';
import { Field as AkField, ErrorMessage as AkErrorMessage } from '@atlaskit/form';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import AkInlineEdit from '@atlaskit/inline-edit';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line import/order
import Tooltip from '@atlaskit/tooltip';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import type { JqlValidationErrors } from '../../../../../../../../../common/types/service.tsx';
import { ReadViewContainer } from '../../../../../../../../../common/ui/goals/styled';
import { Column, Row } from '../../../../../../../../../common/ui/styled';
import { toGoalJqlFieldName } from '../../../../../../../../../common/utils/goal/field/index.tsx';
import { useSLAHierarchicalGoalsEnabled } from '../../../../../../../../../controllers/enable-sla-hierarchical-goals';
import { JqlEditor } from './main';
import messages from './messages';

type Props = {
	isEditing: boolean;
	defaultGoal: boolean;
	focusOnEdit?: boolean;
	areSubGoalsPresent?: boolean;
	id: string;
	metricId?: number;
	jqlValidationError?: JqlValidationErrors;
	validateJQL?: () => void;
} & Intl & {
		jqlQuery: string;
		onEditRequested: () => void;
	};

const JqlFieldView = ({
	intl: { formatMessage },
	id,
	defaultGoal,
	isEditing,
	focusOnEdit = false,
	jqlValidationError,
	jqlQuery,
	metricId,
	areSubGoalsPresent = false,
	onEditRequested,
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	validateJQL = () => {},
}: Props) => {
	const fieldName = toGoalJqlFieldName(id, isEditing);
	const [{ isPriorityNameChanged }] = useSLAHierarchicalGoalsEnabled();

	useEffect(() => {
		// @ts-expect-error - TS2339 - Property 'blur' does not exist on type 'Element'.
		!isEditing && document?.activeElement?.blur();
	}, [isEditing]);

	const debouncedValidateJQL = debounce(validateJQL, 2000);

	if (defaultGoal) {
		return (
			// @ts-expect-error - TS2739 - Type '{ children: string; }' is missing the following properties from type 'Readonly<ThemedOuterStyledProps<ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & { editMode: boolean; placeholder: any; }, any>>': placeholder, editMode
			<DefaultJqlViewContainer>
				{formatMessage(messages.allRemainingIssues)}
			</DefaultJqlViewContainer>
		);
	}

	const accessibleIdForJqlValidationError = 'accessible-id-for-jql-validation-error';
	return (
		<AkField name={fieldName} defaultValue={jqlQuery}>
			{({ fieldProps: { value = '', onChange } }) => (
				<>
					<JQLContainer>
						<Column>
							{/* @ts-expect-error - TS2741 - Property 'defaultValue' is missing in type '{ isEditing: boolean; editView: () => Element; readView: () => Element; onEdit: () => void; onConfirm: () => void; hideActionButtons: true; readViewFitContainerWidth: true; }' but required in type 'InlineEditProps<string>'. */}
							<AkInlineEdit
								isEditing={isEditing}
								editView={() => (
									<EditViewContainer>
										<JqlEditor
											autoFocus={focusOnEdit}
											ariaDescribedBy={accessibleIdForJqlValidationError}
											jql={value}
											onJqlChanged={(...args) => {
												onChange(...args);
												debouncedValidateJQL?.();
											}}
										/>
									</EditViewContainer>
								)}
								readView={() => (
									<ReadViewContainer
										data-testid="servicedesk-sla-settings.ui.metrics.metric.editable.form.goals.goal.fields.jql.read-view-container"
										editMode
										placeholder={value ? 0 : 1}
									>
										{value || formatMessage(messages.newJql)}
									</ReadViewContainer>
								)}
								onEdit={onEditRequested}
								// eslint-disable-next-line @typescript-eslint/no-empty-function
								onConfirm={() => {}}
								hideActionButtons
								readViewFitContainerWidth
							/>
						</Column>
						<Row>
							{
								// @ts-expect-error - TS7053
								isPriorityNameChanged?.[metricId]?.[id] && (
									<Tooltip content={formatMessage(messages.errorTooltip)} position="top">
										<Button
											iconBefore={
												<ErrorIcon
													primaryColor={token('color.icon.danger', colors.R500)}
													size="medium"
													label={formatMessage(messages.errorTooltip)}
												/>
											}
											appearance="subtle"
										/>
									</Tooltip>
								)
							}
							{value.toLowerCase().includes('priority') && areSubGoalsPresent && (
								<Tooltip
									content={formatMessage(messages.warningWhenPriorityIsPresentInJqlWithSubGoals)}
									position="top"
								>
									<Button
										iconBefore={
											<WarningIcon
												primaryColor={token('color.icon.warning', colors.Y500)}
												size="medium"
												label={formatMessage(
													messages.warningWhenPriorityIsPresentInJqlWithSubGoals,
												)}
											/>
										}
										appearance="subtle"
									/>
								</Tooltip>
							)}
						</Row>
					</JQLContainer>
					{jqlValidationError !== null && (
						<div id={accessibleIdForJqlValidationError}>
							<AkErrorMessage>{jqlValidationError}</AkErrorMessage>
						</div>
					)}
				</>
			)}
		</AkField>
	);
};

export const JqlField = injectIntl(JqlFieldView);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const EditViewContainer = styled.div({
	zIndex: 300,
	width: '100%',
	marginTop: token('space.0', '0px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const JQLContainer = styled(Row)({
	alignItems: 'baseline',
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DefaultJqlViewContainer: React.ComponentType<{ editMode: boolean; placeholder: any }> =
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
	styled(ReadViewContainer)({
		paddingTop: token('space.100', '8px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		lineHeight: `${5 * gridSize}px`,
		paddingLeft: token('space.100', '8px'),
	});
